import { Bodies, DistanceJoint, World, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";

/**
 * A waving flag woven from soft `DistanceJoint` springs: a grid of point masses
 * tied to its neighbours (structural + diagonal shear springs) and pinned along
 * the pole edge. A travelling wind force ripples it like real cloth, and a
 * shaded mesh is drawn over the spring network. Grab anywhere to pull the fabric
 * around — it springs back. A taste of the soft-body work on the roadmap.
 */
export const flag: Demo = {
	id: "flag",
	title: "Flag",
	blurb:
		"A cloth flag stitched from hundreds of soft DistanceJoint springs, pinned to its pole and rippled by a travelling wind. Grab the fabric to pull and stretch it — it springs back.",
	category: "Playground & Toys",
	build(): Scene {
		const world = new World({ gravity: [0, 4] });

		const cols = 16;
		const rows = 11;
		const cell = 0.5;
		const x0 = -((cols - 1) * cell) / 2 + 1;
		const y0 = -((rows - 1) * cell) / 2;

		// One little mass per grid point. They never collide with each other —
		// the springs alone give the cloth its shape.
		const nodes: Body[][] = [];
		for (let r = 0; r < rows; r++) {
			const row: Body[] = [];
			for (let c = 0; c < cols; c++) {
				const pinned = c === 0; // pole edge
				row.push(
					world.add(
						Bodies.circle({
							position: [x0 + c * cell, y0 + r * cell],
							radius: 0.025,
							density: 1,
							isStatic: pinned,
							filter: { group: -1 },
						}),
					),
				);
			}
			nodes.push(row);
		}

		// Weave springs: structural (N/E neighbours) keep the weave, diagonal
		// shear springs stop it collapsing. Soft + lightly damped so it ripples.
		const link = (a: Body, b: Body, freq: number): void => {
			world.addConstraint(new DistanceJoint(a, b, { frequency: freq, dampingRatio: 0.4 }));
		};
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				const n = nodes[r]![c]!;
				if (c + 1 < cols) link(n, nodes[r]![c + 1]!, 9);
				if (r + 1 < rows) link(n, nodes[r + 1]![c]!, 9);
				if (c + 1 < cols && r + 1 < rows) link(n, nodes[r + 1]![c + 1]!, 6);
				if (c + 1 < cols && r - 1 >= 0) link(n, nodes[r - 1]![c + 1]!, 6);
			}
		}

		// Travelling wind: a steady gust to the right plus a wave that sweeps
		// across the cloth, scaled by each node's mass for uniform acceleration.
		let t = 0;
		const onBeforeStep = (dt: number): void => {
			t += dt;
			for (let r = 0; r < rows; r++) {
				for (let c = 0; c < cols; c++) {
					const n = nodes[r]![c]!;
					if (n.isStatic) continue;
					const span = 0.3 + 0.7 * (c / cols);
					const wave = Math.sin(t * 4.5 - c * 0.7 - r * 0.3);
					const ax = 6 + 16 * (0.5 + 0.5 * wave) * span;
					const ay = 11 * span * Math.sin(t * 5.5 - c * 0.8 - r * 0.2);
					n.applyForce([n.mass * ax, n.mass * ay]);
				}
			}
		};

		// Draw the cloth as a shaded quad mesh over the spring network. Folds
		// (horizontally compressed cells) are shaded darker to fake lighting.
		const draw = (ctx: CanvasRenderingContext2D, cam: Camera): void => {
			const rest = cell * cam.scale;
			for (let r = 0; r < rows - 1; r++) {
				for (let c = 0; c < cols - 1; c++) {
					const tl = nodes[r]![c]!;
					const tr = nodes[r]![c + 1]!;
					const br = nodes[r + 1]![c + 1]!;
					const bl = nodes[r + 1]![c]!;
					const tlx = cam.worldToScreenX(tl.position.x);
					const tly = cam.worldToScreenY(tl.position.y);
					const trx = cam.worldToScreenX(tr.position.x);
					const trry = cam.worldToScreenY(tr.position.y);
					const brx = cam.worldToScreenX(br.position.x);
					const bry = cam.worldToScreenY(br.position.y);
					const blx = cam.worldToScreenX(bl.position.x);
					const bly = cam.worldToScreenY(bl.position.y);

					// Shade by how compressed the top edge is (a fold turned edge-on).
					const w = Math.hypot(trx - tlx, trry - tly);
					const shade = Math.max(0.25, Math.min(1, w / rest));
					// Two-tone stripes across the flag.
					const band = Math.floor(c / 3) % 2 === 0;
					const hue = band ? 198 : 12;
					const sat = band ? 80 : 85;
					const light = (band ? 56 : 54) * shade + 6;
					ctx.fillStyle = `hsl(${hue} ${sat}% ${light}%)`;
					ctx.beginPath();
					ctx.moveTo(tlx, tly);
					ctx.lineTo(trx, trry);
					ctx.lineTo(brx, bry);
					ctx.lineTo(blx, bly);
					ctx.closePath();
					ctx.fill();
				}
			}

			// The pole.
			const px = cam.worldToScreenX(x0) - 6;
			ctx.strokeStyle = "#8a93a8";
			ctx.lineWidth = 5;
			ctx.lineCap = "round";
			ctx.beginPath();
			ctx.moveTo(px, cam.worldToScreenY(y0 - 0.6));
			ctx.lineTo(px, cam.worldToScreenY(y0 + (rows - 1) * cell + 1.2));
			ctx.stroke();
		};

		return {
			world,
			camera: new Camera(1, 0, 56),
			onBeforeStep,
			draw,
			hint: "A wind ripples the cloth — grab anywhere to pull and stretch the fabric; it springs back.",
		};
	},
};
